import React, { useState, useEffect, useRef } from 'react';
import { Manga, MangaSource } from '../types/manga';
import { downloadChapter, DownloadProgress } from '../services/chapterDownloader';
import { getDownloadedChapters } from '../services/mangaOfflineDB';
import {
  X,
  Download,
  Globe,
  Loader2,
  CheckCircle2,
  AlertCircle,
  StopCircle,
  Layers,
  Sparkles,
  BookOpen,
} from 'lucide-react';
import { showConfirm, showAlert } from './ConfirmDialog';

interface OfflineDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  manga: Manga | null;
  onDownloadComplete?: () => void;
  /** Jump straight into the reader once chapters are downloaded */
  onOpenReader?: (chapterNum: number, availableChapterNums: number[]) => void;
}

export const OfflineDownloadModal: React.FC<OfflineDownloadModalProps> = ({
  isOpen,
  onClose,
  manga,
  onDownloadComplete,
  onOpenReader,
}) => {
  const [selectedSourceIdx, setSelectedSourceIdx] = useState<number>(0);
  // Kept as text so a box can be emptied while typing (mobile backspace); clamped on blur
  const [fromText, setFromText] = useState('1');
  const [toText, setToText] = useState('1');
  const [downloadedSet, setDownloadedSet] = useState<Set<number>>(new Set());

  // Download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState<DownloadProgress | null>(null);
  const [completedList, setCompletedList] = useState<number[]>([]);
  const [failedList, setFailedList] = useState<{ ch: number; reason: string }[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Initialize chapter ranges & sources
  useEffect(() => {
    if (!isOpen || !manga) return;

    // Load existing downloaded chapters
    getDownloadedChapters(manga.id)
      .then((list) => {
        setDownloadedSet(new Set(list.map((c) => c.chapterNum)));
      })
      .catch(() => {});

    // Set default range to next unread chapters or latest chapters
    const read = Number(manga.readCh) || 0;
    const current = Number(manga.currentCh) || 0;

    let start = read > 0 && read < current ? read + 1 : Math.max(1, current - 4);
    let end = Math.min(start + 4, current > 0 ? current : start);

    if (start > end) start = end;

    setFromText(String(start));
    setToText(String(end));
    setSelectedSourceIdx(0);
    setProgress(null);
    setCompletedList([]);
    setFailedList([]);
  }, [isOpen, manga?.id]);

  if (!isOpen || !manga) return null;

  const sources = manga.sources || [];
  const maxChapter = Number(manga.currentCh) || 0;
  const fromChapter = parseInt(fromText) || 0;
  const toChapter = parseInt(toText) || 0;
  const rangeCount = fromChapter > 0 && toChapter >= fromChapter ? toChapter - fromChapter + 1 : 0;

  // Digits only, never above the latest chapter on the card
  const sanitizeChapter = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    const n = parseInt(digits);
    return String(maxChapter > 0 ? Math.min(n, maxChapter) : n);
  };
  const clampOnBlur = (text: string, fallback: number) => {
    const n = parseInt(text) || 0;
    return String(n >= 1 ? n : Math.max(1, fallback));
  };
  const selectedSource: MangaSource | undefined = sources[selectedSourceIdx] || sources[0];

  const handleStartDownload = async () => {
    if (!selectedSource) {
      showAlert('ยังไม่ได้เลือกเว็บ', 'เลือกเว็บแหล่งข้อมูลสำหรับดาวน์โหลดก่อน');
      return;
    }

    const start = fromChapter;
    const end = toChapter;

    if (start <= 0 || end <= 0) {
      showAlert('เลขตอนไม่ถูกต้อง', 'ระบุเลขตอนตั้งแต่ 1 ขึ้นไป');
      return;
    }
    if (end < start) {
      showAlert('ช่วงตอนไม่ถูกต้อง', `"ถึงตอนที่" ต้องไม่น้อยกว่าตอนที่ ${start}`);
      return;
    }

    const count = end - start + 1;
    if (count > 30) {
      if (
        !(await showConfirm({
          title: `ดาวน์โหลด ${count} ตอน?`,
          message: 'อาจใช้เวลาหลายนาทีและใช้พื้นที่หลายร้อย MB',
          confirmLabel: 'ดาวน์โหลดเลย',
        }))
      ) {
        return;
      }
    }

    setIsDownloading(true);
    setCompletedList([]);
    setFailedList([]);
    abortControllerRef.current = new AbortController();

    const chaptersToDownload: number[] = [];
    for (let ch = start; ch <= end; ch++) {
      chaptersToDownload.push(ch);
    }

    for (let i = 0; i < chaptersToDownload.length; i++) {
      const chNum = chaptersToDownload[i];
      if (abortControllerRef.current?.signal.aborted) break;

      try {
        await downloadChapter({
          mangaId: manga.id,
          mangaTitle: manga.title,
          chapterNum: chNum,
          sourceUrl: selectedSource.url,
          sourceName: selectedSource.name || 'เว็บหลัก',
          // Sites that already have this chapter first, so a lagging site doesn't fail the download
          fallbackSources: sources
            .filter((s) => s !== selectedSource && s.url)
            .sort((a, b) => (b.chapter || 0) - (a.chapter || 0))
            .map((s) => ({ url: s.url, name: s.name || s.url })),
          onProgress: (p) => setProgress(p),
          abortSignal: abortControllerRef.current.signal,
        });

        setCompletedList((prev) => [...prev, chNum]);
        setDownloadedSet((prev) => new Set([...prev, chNum]));
      } catch (err: any) {
        if (err.name === 'AbortError' || err.message === 'ยกเลิกการดาวน์โหลด') {
          break;
        }
        setFailedList((prev) => [...prev, { ch: chNum, reason: err.message }]);
      }
    }

    setIsDownloading(false);
    onDownloadComplete?.();
  };

  const handleCancelDownload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsDownloading(false);
  };

  // Start reading from the first chapter of the batch that just finished
  const firstCompleted = completedList.length > 0 ? Math.min(...completedList) : null;
  const handleReadNow = () => {
    if (firstCompleted == null || !onOpenReader) return;
    onOpenReader(firstCompleted, [...downloadedSet].sort((a, b) => a - b));
  };

  // Quick preset helpers
  const handlePresetUnread = () => {
    const read = Number(manga.readCh) || 0;
    const current = Number(manga.currentCh) || 0;
    if (read < current) {
      setFromText(String(read + 1));
      setToText(String(Math.min(read + 10, current)));
    }
  };

  const handlePresetLatest = (count: number) => {
    const current = Number(manga.currentCh) || 0;
    if (current > 0) {
      setFromText(String(Math.max(1, current - count + 1)));
      setToText(String(current));
    }
  };

  return (
    <div className="modal-overlay safe-area-modal">
      <div onClick={(e) => e.stopPropagation()} className="modal-panel max-w-md max-h-[92vh]">
        {/* Header */}
        <div className="modal-header">
          <div className="min-w-0">
            <h3 className="modal-title">ดาวน์โหลดอ่านออฟไลน์</h3>
            <p className="text-xs text-zinc-500 truncate mt-0.5">{manga.title}</p>
          </div>
          {!isDownloading && (
            <button onClick={onClose} title="ปิด" className="icon-btn -mr-2">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="modal-body space-y-5" style={{ WebkitOverflowScrolling: 'touch' }}>
          <p className="text-xs text-zinc-500">
            ตอนล่าสุดในระบบ <span className="text-zinc-300 tabular-nums">{manga.currentCh}</span> · อ่านแล้ว{' '}
            <span className="text-zinc-300 tabular-nums">{manga.readCh || 0}</span>
          </p>

          {/* 1. Select Source */}
          <div>
            <label className="field-label flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" />
              <span>เว็บแหล่งข้อมูล (เลือกเว็บที่ภาพคมชัด)</span>
            </label>
            {sources.length > 0 ? (
              <select
                disabled={isDownloading}
                value={selectedSourceIdx}
                onChange={(e) => setSelectedSourceIdx(Number(e.target.value))}
                className="field"
              >
                {sources.map((s, idx) => (
                  <option key={idx} value={idx}>
                    {s.name || s.url} {s.chapter ? `(ถึงตอนที่ ${s.chapter})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-rose-400">ยังไม่มีลิงก์เว็บที่บันทึกไว้สำหรับเรื่องนี้</p>
            )}
          </div>

          {/* 2. Select Chapter Range */}
          <div>
            <label className="field-label flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" />
              <span>ช่วงตอนที่จะดาวน์โหลด</span>
            </label>

            <div className="flex items-end gap-2">
              <div className="flex-1">
                <span className="text-[11px] text-zinc-500 block mb-1">ตั้งแต่ตอนที่</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  disabled={isDownloading}
                  value={fromText}
                  onChange={(e) => setFromText(sanitizeChapter(e.target.value))}
                  onBlur={() => {
                    const from = clampOnBlur(fromText, 1);
                    setFromText(from);
                    // Push "to" up so the range never runs backwards
                    if (toChapter > 0 && toChapter < Number(from)) setToText(from);
                  }}
                  className="field tabular-nums"
                />
              </div>

              <div className="pb-2 text-zinc-600">→</div>

              <div className="flex-1">
                <span className="text-[11px] text-zinc-500 block mb-1">
                  ถึงตอนที่{maxChapter > 0 && <span className="text-zinc-600"> (สูงสุด {maxChapter})</span>}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  disabled={isDownloading}
                  value={toText}
                  onChange={(e) => setToText(sanitizeChapter(e.target.value))}
                  onBlur={() => {
                    const to = Number(clampOnBlur(toText, maxChapter));
                    setToText(String(Math.max(to, fromChapter)));
                  }}
                  className="field tabular-nums"
                />
              </div>
            </div>

            {/* Quick Presets */}
            {!isDownloading && (
              <div className="flex flex-wrap items-center gap-1.5 mt-3">
                <button type="button" onClick={handlePresetUnread} className="chip chip-off h-7">
                  ตอนที่ยังไม่อ่าน
                </button>
                <button type="button" onClick={() => handlePresetLatest(5)} className="chip chip-off h-7">
                  5 ตอนล่าสุด
                </button>
                <button type="button" onClick={() => handlePresetLatest(10)} className="chip chip-off h-7">
                  10 ตอนล่าสุด
                </button>
              </div>
            )}
          </div>

          {/* Progress / Status display during download */}
          {isDownloading && progress && (
            <div className="panel p-3.5 space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-zinc-100 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-zinc-400" />
                  <span>กำลังดาวน์โหลดตอนที่ {progress.chapterNum}...</span>
                </span>
                {progress.totalImages > 0 && (
                  <span className="text-zinc-500 tabular-nums text-[11px]">
                    {progress.currentImage} / {progress.totalImages} ภาพ
                  </span>
                )}
              </div>

              {/* Progress bar */}
              <div className="w-full bg-white/[0.06] rounded-full h-1 overflow-hidden">
                <div
                  className="bg-violet-400 h-full rounded-full transition-all duration-150"
                  style={{
                    width: `${
                      progress.totalImages > 0
                        ? Math.round((progress.currentImage / progress.totalImages) * 100)
                        : 5
                    }%`,
                  }}
                />
              </div>

              <p className="text-[11px] text-zinc-500 truncate">{progress.message}</p>
            </div>
          )}

          {/* Results summary after completion */}
          {!isDownloading && (completedList.length > 0 || failedList.length > 0) && (
            <div className="panel p-3.5 space-y-2">
              {completedList.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-zinc-300">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                  <span>
                    ดาวน์โหลดสำเร็จ {completedList.length} ตอน:{' '}
                    <span className="text-zinc-100 font-medium">{completedList.join(', ')}</span>
                  </span>
                </div>
              )}
              {failedList.length > 0 && (
                <div className="flex items-start gap-2 text-xs text-zinc-300">
                  <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                  <div>
                    <span>ไม่สามารถดาวน์โหลดได้ {failedList.length} ตอน:</span>
                    <ul className="list-disc pl-4 mt-1 space-y-0.5 text-[11px] text-zinc-500">
                      {failedList.map((f, idx) => (
                        <li key={idx}>
                          ตอนที่ {f.ch}: {f.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          {isDownloading ? (
            <button type="button" onClick={handleCancelDownload} className="btn btn-danger ring-1 ring-inset ring-rose-500/25">
              <StopCircle className="w-4 h-4" />
              <span>หยุดดาวน์โหลด</span>
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="btn btn-ghost">
                ปิด
              </button>
              <button
                type="button"
                onClick={handleStartDownload}
                className={`btn ${firstCompleted != null && onOpenReader ? 'btn-secondary' : 'btn-primary'}`}
              >
                <Download className="w-4 h-4" />
                <span>
                  {firstCompleted != null && onOpenReader ? 'โหลดเพิ่ม' : 'เริ่มดาวน์โหลด'} ({rangeCount} ตอน)
                </span>
              </button>
              {firstCompleted != null && onOpenReader && (
                <button type="button" onClick={handleReadNow} className="btn btn-primary">
                  <BookOpen className="w-4 h-4" />
                  <span>อ่านตอน {firstCompleted}</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
